/**
 * SaveSyncService — Syncs save slots and custom maps with the remote API when the user is logged in.
 *
 * - On first login with no remote data: one-time upload of local saves and custom maps.
 * - When remote has newer data: replace local with remote.
 * - When both local and remote have data: show conflict dialog (overwrite account, use account, or cancel login).
 * - When user saves locally: push to remote (debounced).
 *
 * Uses localStorage key pjm_remote_updated_at to compare with server updated_at.
 */

import { AuthService } from './AuthService';
import { SaveManager } from './SaveManager';
import { CustomLevelManager } from './CustomLevelManager';
import { ProgressState } from '../state/ProgressState';

const REMOTE_UPDATED_AT_KEY = 'pjm_remote_updated_at';
const SAVE_MANAGER_KEY = 'pjm_saves';
const SUPPRESS_PUSH_MS = 2500;

export interface SyncConflictActions {
  /** Overwrite the account's saved progress with this device's local progress, then continue logged in. */
  overwriteWithLocal: () => Promise<void>;
  /** Replace this device's progress with the account's saved progress, then continue logged in. */
  useAccount: () => Promise<void>;
  /** Log out and keep this device's local progress (don't sync). */
  cancelLogin: () => Promise<void>;
}

type SyncConflictHandler = (actions: SyncConflictActions) => void;

class _SaveSyncService {
  private _pushTimeout: ReturnType<typeof setTimeout> | null = null;
  private _suppressPushUntil = 0;
  private _initDone = false;
  private _conflictHandler: SyncConflictHandler | null = null;

  /** Set the handler that shows the sync-conflict dialog (e.g. from MenuScene). Call once at startup. */
  setConflictHandler(handler: SyncConflictHandler): void {
    this._conflictHandler = handler;
  }

  /** Call once after AuthService is ready (e.g. from BootScene or MenuScene). */
  init(): void {
    if (this._initDone) return;
    this._initDone = true;

    // Page load with existing session: sync without showing conflict dialog
    if (AuthService.ready && AuthService.isLoggedIn) {
      this._sync(false);
    }

    // Just logged in (e.g. after Google sign-in): sync and show conflict if both have data and they differ
    AuthService.onChange((user) => {
      if (user) this._sync(true);
    });
  }

  /**
   * Call when save data or custom maps have been written locally.
   * Debounced; only pushes if user is logged in.
   */
  schedulePush(): void {
    if (!AuthService.isLoggedIn) return;
    if (Date.now() < this._suppressPushUntil) return;

    if (this._pushTimeout) clearTimeout(this._pushTimeout);
    this._pushTimeout = setTimeout(() => {
      this._pushTimeout = null;
      this._push();
    }, 1000);
  }

  private get _apiBase(): string {
    return AuthService.authApiBase;
  }

  /**
   * @param showConflictIfBoth — true only when user just logged in; show conflict dialog if both local and remote have data and they differ. false on page load (existing session).
   */
  private async _sync(showConflictIfBoth: boolean): Promise<void> {
    try {
      const res = await fetch(`${this._apiBase}/api/saves.php`, { credentials: 'include' });
      if (!res.ok) return;

      const data = (await res.json()) as {
        save_data: { currentSlotId?: string; slots?: Record<string, unknown> } | null;
        custom_maps_data?: unknown[];
        updated_at: string | null;
      };

      const remoteUpdatedAt = data.updated_at ?? null;
      const localUpdatedAt = localStorage.getItem(REMOTE_UPDATED_AT_KEY);
      const hasLocal = SaveManager.hasSaves() || CustomLevelManager.getAllLevels().length > 0;

      const hasRemote = data.save_data != null && Object.keys(data.save_data?.slots ?? {}).length > 0
        || (Array.isArray(data.custom_maps_data) && data.custom_maps_data.length > 0);

      // Data is already in sync (same timestamp) — no conflict
      const sameAsRemote = localUpdatedAt && remoteUpdatedAt && localUpdatedAt === remoteUpdatedAt;

      // Conflict only when: just logged in, both have data, handler set, and data actually differs
      if (showConflictIfBoth && hasRemote && hasLocal && !sameAsRemote && this._conflictHandler) {
        const remoteSaveData = data.save_data;
        const remoteMapsData = data.custom_maps_data ?? [];
        const remoteUpdatedAtStr = remoteUpdatedAt ?? '';

        this._conflictHandler({
          overwriteWithLocal: async () => {
            console.log('[SaveSyncService] User chose: overwrite account with local progress');
            await this._push();
          },
          useAccount: async () => {
            console.log('[SaveSyncService] User chose: use account progress');
            this._applyRemoteToLocal(remoteSaveData, remoteMapsData, remoteUpdatedAtStr);
          },
          cancelLogin: async () => {
            console.log('[SaveSyncService] User chose: cancel login');
            await AuthService.logout();
          },
        });
        return;
      }

      if (hasRemote && remoteUpdatedAt) {
        const remoteNewer = !localUpdatedAt || new Date(remoteUpdatedAt).getTime() > new Date(localUpdatedAt).getTime();
        if (remoteNewer) {
          this._applyRemoteToLocal(data.save_data, data.custom_maps_data ?? [], remoteUpdatedAt);
          return;
        }
      }

      // No remote data (or empty): one-time upload from local if we have local saves
      if (!hasRemote && hasLocal) {
        console.log('[SaveSyncService] No remote data; uploading local saves (one-time sync)');
        await this._push();
        console.log('[SaveSyncService] One-time sync complete — saves are now on the server');
      }
    } catch (e) {
      console.warn('[SaveSyncService] Sync check failed:', e);
    }
  }

  private _applyRemoteToLocal(
    saveData: { currentSlotId?: string; slots?: Record<string, unknown> } | null,
    customMapsData: unknown[],
    updatedAt: string
  ): void {
    if (saveData && typeof saveData.currentSlotId === 'string' && saveData.slots && typeof saveData.slots === 'object') {
      localStorage.setItem(SAVE_MANAGER_KEY, JSON.stringify(saveData));
      SaveManager.reloadFromStorage();
    }
    if (Array.isArray(customMapsData) && customMapsData.length >= 0) {
      const levels = customMapsData.filter(
        (x): x is import('./CustomLevelManager').CustomLevel =>
          x != null && typeof x === 'object' && 'id' in x && 'name' in x
      );
      CustomLevelManager.replaceAllLevels(levels);
    }
    ProgressState.reload();
    localStorage.setItem(REMOTE_UPDATED_AT_KEY, updatedAt);
    this._suppressPushUntil = Date.now() + SUPPRESS_PUSH_MS;
    console.log('[SaveSyncService] Applied remote save data — local state updated from server');
  }

  private async _push(): Promise<void> {
    if (!AuthService.isLoggedIn) return;

    const exportPayload = SaveManager.exportSaveData();
    const parsed = JSON.parse(exportPayload) as { currentSlotId: string; slots: Record<string, unknown>; customLevels?: unknown[] };
    const payload = {
      save_data: { currentSlotId: parsed.currentSlotId, slots: parsed.slots },
      custom_maps_data: Array.isArray(parsed.customLevels) ? parsed.customLevels : CustomLevelManager.getAllLevels(),
    };

    try {
      const res = await fetch(`${this._apiBase}/api/saves.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      if (!res.ok) return;
      const result = (await res.json()) as { updated_at?: string };
      if (result.updated_at) {
        localStorage.setItem(REMOTE_UPDATED_AT_KEY, result.updated_at);
      }
    } catch (e) {
      console.warn('[SaveSyncService] Push failed:', e);
    }
  }
}

// Expose internal for building payload without duplicating export logic; SaveManager.exportSaveData() already has slots + customLevels
export const SaveSyncService = new _SaveSyncService();
